import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateLexiconConceptApiLexiconConceptsUniquePut, type UpdateLexiconConceptRequest } from "@noa/api-core";
import { reportError } from "../../typed-client";
import { ApiError } from "../../errors";
import { LexiconQueryKey } from "../../query-keys";

type UpdateLexiconConceptParams = {
  unique: string;
  body: UpdateLexiconConceptRequest;
  /** The concept's subjects before the edit, so a subject reassignment also refreshes the old list. */
  previousSubjectEn: string | null;
  previousSubjectFr: string | null;
};

const putLexicalConcept = async ({ unique, body }: UpdateLexiconConceptParams) => {
  const { data } = await updateLexiconConceptApiLexiconConceptsUniquePut({
    path: { unique },
    body,
    throwOnError: true,
  });
  return data;
};

export const useUpdateLexiconConcept = () => {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof putLexicalConcept>>, ApiError, UpdateLexiconConceptParams>({
    mutationFn: putLexicalConcept,
    onError: reportError,
    onSuccess: (_data, { body, previousSubjectEn, previousSubjectFr }) => {
      void queryClient.invalidateQueries({ queryKey: [...LexiconQueryKey.all, "subjects"] });
      // Invalidate both the new and previous subjects (deduped) so a reassignment refreshes both lists.
      const affectedSubjects = new Set(
        [body.subject_fr, body.subject_en, previousSubjectFr, previousSubjectEn].filter(
          (subject): subject is string => !!subject
        )
      );
      affectedSubjects.forEach((subject) => {
        void queryClient.invalidateQueries({ queryKey: LexiconQueryKey.conceptsBySubject(subject) });
      });
    },
  });
};
